import { App as CapacitorApp } from "@capacitor/app";
import { Keyboard } from "@capacitor/keyboard";
import { SplashScreen } from "@capacitor/splash-screen";
import { Style as SBStyle, StatusBar } from "@capacitor/status-bar";
import * as Sentry from "@sentry/browser";
import { Shortcut, SiriShortcuts } from "capacitor-plugin-siri-shorts";
import { updateBadgeCount } from "studyGoals";
import Device from "./device";
import Globals from "./globals";
import L10n, { userLocale } from "./localization";
import Notification from "./notification";
import Style from "./style";

import { Capacitor } from "@capacitor/core";
import mount from "entryPoint";
import FeatureFlags from "featureFlags";

import IDB from "@data/idb";
import { syncOperationsAndRefetchRemoteDecks } from "@data/remoteDecks";
import { defineCustomElements } from "@ionic/pwa-elements/loader";
import { User } from "@models/user";
import sentryBeforeSend from "errors/sentryBeforeSend";
import EventBus from "eventBus";
import { initializeResponseHistory } from "hooks/data/responseHistory";
import { superCache } from "hooks/data/superCache";
import { Locales } from "locales";
import "../app.css";
import { shipAppleIAPsToServerIfNecessary } from "iap";
import { registerLocalNotificationActionListener } from "localnotifs";
import { registerGlobalErrorHandlers } from "./errors/global";

// See https://capacitorjs.com/docs/web/pwa-elements.
defineCustomElements(window);

interface IWindow extends Window {
  devicePixelRatio: number;
  device: any;
  __immediateLogout: () => any;
  __syncOperationsNow: () => any;
}

declare let window: IWindow;

function handleSiriShortcut(shortcut: Shortcut) {
  const pID = shortcut.persistentIdentifier;
  if (pID.startsWith("review-deck-") && shortcut.deckId) {
    window.location.pathname = `/decks/local/${shortcut.deckId}/review`;
  } else if (pID.startsWith("review-group-") && shortcut.deckTagName) {
    window.location.pathname = `/folders/${shortcut.deckTagName}/review`;
  }
}

const onResume = () => {
  if (Globals.platform !== "android") {
    // NOTE: without this guard, was preventing Android color scheme setting from persisting after app switching.
    Style.setTheme();
  }
};

// biome-ignore lint/complexity/noStaticOnlyClass: <explanation>
class App {
  static async init() {
    // NOTE: must do this before calling database init, because that checks if the Device's token is set.
    await Device.init();
    try {
      await IDB.init();
    } catch (e) {
      Notification.alert(L10n.localize((s) => s.error.databaseUnOpenable));
      return;
    }

    registerLocalNotificationActionListener();

    // Tag Sentry payloads with OS version info we get from Capacitor.
    // Sentry infers OS version info from user agent string and will otherwise
    // assume that Catalyst clients are running "Mac OS X 10_15_6".
    if (Globals.sentryDSN) {
      Sentry.setTag("os.native", `${Device.name} ${Device.version}`);
    }

    if (Capacitor.isPluginAvailable("SplashScreen")) {
      await SplashScreen.hide();
    }

    L10n.switchLocale(userLocale() as Locales);

    mount();

    const userId = localStorage["AnkiApp.user.id"];
    if (userId) {
      Sentry.setUser({ id: userId });
    }

    // NOTE: this initialization is done after the React app mount()'s,
    // so that we can display a progress indicator for initialization.
    try {
      await superCache.init();
    } catch (err) {
      Sentry.captureException(err, { tags: { location: "appInit" } });
      alert(L10n.localize((s) => s.error.databaseUnOpenable));
    }

    if (Capacitor.isPluginAvailable("Keyboard") && Globals.platform === "ios") {
      Keyboard.setAccessoryBarVisible({ isVisible: true }); // Shows the bar with the "Done" button on iPhone.
    }

    localStorage["AnkiApp.version"] = Globals.version;

    updateBadgeCount();

    // Listen for cache events that affect due card counts
    superCache.events.on("knolUpdated", updateBadgeCount);
    superCache.events.on("deckUpdated", updateBadgeCount);
    superCache.events.on("responsesUpdated", updateBadgeCount);
    superCache.events.on("responsesLoaded", updateBadgeCount);
    EventBus.on("responseInserted", updateBadgeCount);

    if (User.isLoggedIn()) {
      FeatureFlags.updateFlagCache().then(async () => {
        await shipAppleIAPsToServerIfNecessary();
      });

      const migrateDecks = async () => {
        await syncOperationsAndRefetchRemoteDecks();
      };
      migrateDecks().catch(() => {
        // Uh oh.
      });
      initializeResponseHistory();
    }

    Device.onResume(true);
  }

  static captureExternalLinks(e: MouseEvent) {
    const findLink = (node: Node | null): HTMLAnchorElement | null => {
      if (node) {
        if (node.nodeName === "A") {
          return node as HTMLAnchorElement;
        }
        return findLink(node.parentNode);
      }
      return null;
    };

    const link = findLink(e.target as Node);
    if (
      link &&
      link.href !== "" &&
      link.href.indexOf("mailto:") !== 0 &&
      link.href.indexOf(`${location.protocol}//${location.hostname}`) !== 0
    ) {
      e.stopPropagation();
      e.preventDefault();
      Device.openExternalLink(link.href);
      return false;
    }
  }
}

async function onCordovaPluginsReady() {
  await App.init();

  document.addEventListener("online", () => Device.onResume(true), false);
}

function nativePlatformInit() {
  document.addEventListener("resume", onResume, false);
  document.addEventListener("deviceready", onCordovaPluginsReady, false);

  if (Capacitor.isPluginAvailable("SiriShortcuts")) {
    SiriShortcuts.addListener("appLaunchBySiriShortcuts", handleSiriShortcut);
  }

  // Listen for app state changes to update badge count when app comes to foreground
  if (Capacitor.isPluginAvailable("App")) {
    CapacitorApp.addListener("appStateChange", ({ isActive }) => {
      if (isActive) {
        updateBadgeCount();
      }
    });
  }

  if (Capacitor.isPluginAvailable("StatusBar")) {
    if (Globals.platform === "android") {
      StatusBar.setOverlaysWebView({ overlay: false });
    }

    StatusBar.show();

    if (Style.currentTheme === "night") {
      StatusBar.setStyle({ style: SBStyle.Dark });
    } else {
      StatusBar.setStyle({ style: SBStyle.Light });
    }
  }
}

const SINGLE_TAB_LOCK_KEY = "ankiapp_single_tab_lock";
const SINGLE_TAB_SESSION = Math.random().toString(36).substr(2) + String(Date.now());
let singleTabActive = false;

/**
 * Sets a lock in localStorage to indicate this tab is using the app.
 */
function claimSingleTabLock() {
  localStorage.setItem(
    SINGLE_TAB_LOCK_KEY,
    JSON.stringify({ session: SINGLE_TAB_SESSION, ts: Date.now() }),
  );
  singleTabActive = true;
}

function releaseSingleTabLock() {
  const lock = localStorage.getItem(SINGLE_TAB_LOCK_KEY);
  if (lock) {
    try {
      const parsed = JSON.parse(lock);
      if (parsed.session === SINGLE_TAB_SESSION) {
        localStorage.removeItem(SINGLE_TAB_LOCK_KEY);
      }
    } catch {}
  }
  singleTabActive = false;
}

/**
 * Checks if another tab appears to be using the app.
 * Returns true if another tab has the lock.
 */
function isAnotherTabActive() {
  const lock = localStorage.getItem(SINGLE_TAB_LOCK_KEY);
  if (!lock) return false;
  try {
    const parsed = JSON.parse(lock);
    if (parsed.session && parsed.session !== SINGLE_TAB_SESSION) {
      // Consider lock valid for 10s to allow for possible tab crashes
      if (Date.now() - parsed.ts < 10000) {
        return true;
      }
    }
  } catch {}
  return false;
}

function showMultiTabWarning() {
  document.body.innerHTML = `
    <div style="position:fixed;top:0;left:0;width:100vw;height:100vh;display:flex;align-items:center;justify-content:center;z-index:999;background-color:#1b1b1b;color:white;font-family:sans-serif;font-size:1.5rem;">
      <div style="text-align:center;">
        <h1><b>${L10n.localize((s) => s.browser.multiTabWarningHeader)}</b></h1>
        <p style="line-height:1.5;">${L10n.localize((s) => s.browser.multiTabWarningBody)}</p>
      </div>
    </div>
  `;
}

window.addEventListener("storage", (e) => {
  if (e.key === SINGLE_TAB_LOCK_KEY && singleTabActive) {
    // Another tab took the lock: show warning and block the UI
    if (isAnotherTabActive()) {
      showMultiTabWarning();
      releaseSingleTabLock();
    }
  }
});

// Renew lock periodically so others know this tab is still active.
setInterval(() => {
  if (singleTabActive) {
    claimSingleTabLock();
  }
}, 5000);

window.addEventListener("beforeunload", () => {
  releaseSingleTabLock();
});

function onDOMLoaded() {
  document.title = L10n.localize((s) => s.general.appName);
  Style.setTheme();

  // TODO: scope this to only components that can have external links. Maybe with a hook that each of those components includes.
  document.addEventListener("click", App.captureExternalLinks, true);

  if (Globals.platform === "web") {
    if (isAnotherTabActive()) {
      showMultiTabWarning();
      return;
    }
    claimSingleTabLock();
    return App.init();
  }
  nativePlatformInit();
}

if (Globals.sentryDSN) {
  Sentry.init({
    sendDefaultPii: false,
    dsn: Globals.sentryDSN,
    release: Globals.sentryRelease,
    beforeSend: sentryBeforeSend,
  });
  Sentry.setTag("app_version", Globals.version);
}

registerGlobalErrorHandlers();

if (document.readyState !== "loading") {
  onDOMLoaded();
} else {
  document.addEventListener("DOMContentLoaded", onDOMLoaded);
}

export default new App();
